import buildBulletSeries, { toGroupedBulletSeries } from './buildBulletSeries';
import buildTableDefinition, {
  buildRecordsDefinition,
  isRecordsTable,
} from './buildTableDefinition';
import { ChartDefinitionResult, DATE_DIMENSION } from './chartDefinitionParts';
import {
  buildBulletDisplayParts,
  buildDataLabelParts,
  buildStackedAreaOptions,
  buildStackedBarOptions,
  getDisplayCapabilities,
  withSeriesDisplay,
} from './chartDisplayOptions';
import {
  buildIntervalParts,
  buildLimitParts,
  buildSortParts,
  getChartCapabilities,
} from './chartOptions';
import { hasTermDimension, toDimension, toGroupingFlags } from './dimensions';
import {
  getSeriesVariant,
  resolveSeriesMetricId,
  supportsSeriesVariants,
  toQueryOverrides,
} from './seriesVariants';
import {
  getGadgetType,
  getVisType,
  isTimelineHeatmap,
  takesChartColours,
} from './visTypes';

// Builds an in-memory V5 chart definition for a Studio block so it can render
// through V5Gadget. Each vis type needs a different minimum shape (e.g. pie
// needs a dimension, scatter needs two metrics); when the block doesn't meet
// it, returns what's missing instead. Not used for Number blocks, which render
// their own KPI.
const buildChart = ({
  card,
  name,
}: {
  card: Studio.ChartContent;
  name: string;
}): ChartDefinitionResult => {
  const visType = getVisType(card.visType);
  const metricIds = card.metricIds ?? [];
  const [dimA, dimB] = card.dimensions ?? [];
  const dimensionOptions = card.dimensionOptions ?? {};
  // A text field's terms unless its options say otherwise
  const dimension = (field: string): Dimension =>
    toDimension({ field, options: dimensionOptions[field] });

  if (!visType.gadgetType) {
    return { missing: `${visType.label} isn't available in Studio yet` };
  }
  // Records tables list a dataset rather than showing metrics
  if (isRecordsTable(card)) {
    return buildRecordsDefinition({ card, name });
  }
  if (metricIds.length === 0) {
    return { missing: 'Add a metric' };
  }

  // Tables and charts that colour themselves take no palette
  const colors = takesChartColours(card.visType) ? card.chartColors : undefined;
  const metricColors = (colors && colors.metrics) || {};
  // Interval, sorting, limit and display, as chosen (by default the vis
  // type's interval, every term, by the primary metric)
  const options = card.chart ?? {};
  const display = getDisplayCapabilities(card);

  // Each entry of metricIds is a series: a metric, or a variant of one
  // asking its own question (see seriesVariants)
  const canVary = supportsSeriesVariants({ ...card, visType: visType.id });
  const series = metricIds.map((key, index): V5ChartDefinitionSeries => {
    const queryOverrides = canVary
      ? toQueryOverrides(getSeriesVariant({ card, key }))
      : undefined;
    return withSeriesDisplay({
      series: {
        id: `${card.id}-${key}`,
        metricId: resolveSeriesMetricId({ card, key }),
        ...(metricColors[key] ? { color: metricColors[key] } : {}),
        ...(queryOverrides ? { queryOverrides } : {}),
      },
      seriesKey: key,
      index,
      options,
      display,
    });
  });

  const gadgetType = getGadgetType(card) ?? visType.gadgetType;
  const base: V5ChartDefinition = {
    id: card.id,
    name,
    description: card.description,
    gadgetType,
    series,
    ...(colors && colors.palette && colors.palette !== 'default'
      ? { colorPalette: colors.palette }
      : {}),
    ...(gadgetType === 'pie' && options.isSolid ? { isSolid: true } : {}),
  };

  const capabilities = getChartCapabilities(card);
  const dates = buildIntervalParts({ options, capabilities });
  const limit = buildLimitParts({ options, capabilities });

  const grouped = (field: string): Partial<V5ChartDefinition> => ({
    dimensionA: dimension(field),
    ...buildSortParts({ options, capabilities, dimension: field }),
    ...limit,
  });

  const overTime: Partial<V5ChartDefinition> = {
    dimensionA: DATE_DIMENSION,
    ...dates,
  };

  const needsDimension = {
    missing: `Add a dimension to show a ${visType.label}`,
  };

  switch (visType.id) {
    case 'line':
      // One line per metric, or one per term when split by a dimension
      // (splitting only works with a single metric)
      return {
        chartDefinition: {
          ...base,
          ...overTime,
          ...(capabilities.isSplitLine
            ? {
                dimensionB: dimension(dimA),
                ...buildSortParts({ options, capabilities, dimension: dimA }),
                ...limit,
              }
            : {}),
        },
      };
    case 'bar':
    case 'pictorialBar':
    case 'combo':
      // By dimension if there is one, otherwise over time
      return {
        chartDefinition: {
          ...base,
          ...(dimA ? grouped(dimA) : overTime),
          ...buildDataLabelParts({ options, display }),
        },
      };
    case 'pie':
      // One metric's share of each term
      return dimA
        ? {
            chartDefinition: {
              ...base,
              series: series.slice(0, 1),
              ...grouped(dimA),
            },
          }
        : needsDimension;
    case 'radar':
      return dimA
        ? { chartDefinition: { ...base, ...grouped(dimA) } }
        : needsDimension;
    case 'table':
      return buildTableDefinition({ card, base });
    case 'stackedBar':
      if (!dimA) {
        return needsDimension;
      }
      // Stacks by the first dimension, bars by the second (or by month)
      return {
        chartDefinition: {
          ...base,
          series: series.slice(0, 1),
          ...grouped(dimA),
          ...(dimB
            ? { dimensionB: dimension(dimB) }
            : { dimensionB: DATE_DIMENSION, ...dates }),
          stackedBarOptions: buildStackedBarOptions(options),
        },
      };
    case 'area':
      if (!dimA) {
        return needsDimension;
      }
      return {
        chartDefinition: {
          ...base,
          series: series.slice(0, 1),
          ...grouped(dimA),
          dimensionB: DATE_DIMENSION,
          ...dates,
          stackedAreaOptions: buildStackedAreaOptions(options),
        },
      };
    case 'heatmap':
      // A Timeline: a row per metric, a column per date. Without a second
      // dimension, which tells it from a heatmap of two.
      if (isTimelineHeatmap(card)) {
        return { chartDefinition: { ...base, ...overTime } };
      }
      if (!dimA || !dimB) {
        return { missing: 'Add two dimensions to show a Heatmap' };
      }
      return {
        chartDefinition: {
          ...base,
          series: series.slice(0, 1),
          ...grouped(dimA),
          dimensionB: dimension(dimB),
        },
      };
    case 'scatter':
      // x and y, and a third metric sizes the points (a bubble chart)
      if (metricIds.length < 2) {
        return { missing: 'Add two metrics (x and y) to show a Scatter' };
      }
      return dimA
        ? {
            chartDefinition: {
              ...base,
              series: series.slice(0, 3),
              ...grouped(dimA),
            },
          }
        : needsDimension;
    case 'treeMap':
    case 'waterfall':
      // One metric: its share of each term (or date), or the steps to its
      // total
      return {
        chartDefinition: {
          ...base,
          series: series.slice(0, 1),
          ...(dimA ? grouped(dimA) : overTime),
          ...buildDataLabelParts({ options, display }),
        },
      };
    case 'packedBubble':
    case 'funnel':
      return dimA
        ? {
            chartDefinition: {
              ...base,
              series: series.slice(0, 1),
              ...grouped(dimA),
            },
          }
        : needsDimension;
    case 'sankey':
      // Flows from the first dimension's terms to the second's, weighted by
      // one metric. No interval: any turns its sub-grouping off.
      if (!dimA || !dimB) {
        return {
          missing: 'Add two dimensions (from and to) to show a Sankey',
        };
      }
      return {
        chartDefinition: {
          ...base,
          series: series.slice(0, 1),
          dimensionA: dimension(dimA),
          dimensionB: dimension(dimB),
          ...limit,
        },
      };
    case 'regionMap':
      // One metric by a state or province field: every term, so none of the
      // map is left out
      if (!dimA) {
        return { missing: 'Add a state or province dimension to show a Map' };
      }
      return {
        chartDefinition: {
          ...base,
          series: series.slice(0, 1),
          dimensionA: dimension(dimA),
          groupByLimitMode: 'show all',
        },
      };
    case 'bullet': {
      // A bullet per metric, or per term of the first metric (as a bar's
      // terms: sorted and limited by it). Ranges, days and months have no
      // terms to put a bullet on.
      if (dimA && !hasTermDimension(card)) {
        return { missing: 'Group a Bullet by a text or yes/no field' };
      }
      const bulletSeries = buildBulletSeries({ content: card, series });
      if (!bulletSeries.some((s) => !s.bullet?.isTarget)) {
        return { missing: 'Add a metric to measure against its target' };
      }
      const bulletDisplay = buildBulletDisplayParts({ options, display });
      return {
        chartDefinition: dimA
          ? {
              ...base,
              series: toGroupedBulletSeries(bulletSeries),
              ...grouped(dimA),
              ...bulletDisplay,
            }
          : { ...base, series: bulletSeries, ...bulletDisplay },
      };
    }
    case 'trend':
      // The card buckets the dates itself: 30 days, or calendar months
      return {
        chartDefinition: {
          ...base,
          series: series.slice(0, 1),
        },
      };
    default:
      return { missing: `${visType.label} isn't available in Studio yet` };
  }
};

// With the day-of-week and month flags its dimensions need
const buildChartDefinition = (
  args: Parameters<typeof buildChart>[0],
): ChartDefinitionResult => {
  const result = buildChart(args);
  if (!('chartDefinition' in result)) {
    return result;
  }
  const { chartDefinition } = result;
  const flags = toGroupingFlags(
    [chartDefinition.dimensionA, chartDefinition.dimensionB].filter(
      (d): d is Dimension => !!d,
    ),
  );
  return { chartDefinition: { ...chartDefinition, ...flags } };
};

export default buildChartDefinition;
